import { useEffect, useState } from "react";
import { Field, Form, Formik } from "formik";
import { shallow } from "zustand/shallow";
import * as yup from "yup";

import { InputText, InputTextArea } from "../../../../common/UI/Inputs";
import AppGetIcon from "../../../../common/UI/AppGetIcon";
import DataTable from "../../../../common/UI/DataTable/dataTable";
import Modal from "../../../../common/UI/Modal";

import { useHeaderStore } from "../../../../store/header.store";

import style from "./Procedures.module.css";
import styleButton from "../../../../common/theme/styles/button.module.css";
import styleForm from "../../../../common/theme/styles/form.module.css";
import styleDataTable from "../../../../common/UI/DataTable/dataTable.module.css";
import { toast } from "react-toastify";
import { CreateOrUpdatedProcedureService, procedureData } from "../../services/procedures/createOrUpdateProcedure.service";
import { DeleteInstitutionsService } from "../../services/institutions/deleteInstitutions.service";
import { GetProceduresWithPaginationService } from "../../services/procedures/getProceduresWithPagination.service";
import { DeleteProcedureService } from "../../services/procedures/deleteProcedure.service";
import DeleteConfirmationModal from "../../../../common/UI/DataTable/DeleteConfirmationModal/deleteConfirmationModal";

const getProceduresWithPaginationService = new GetProceduresWithPaginationService();
const createOrUpdatedProcedureService = new CreateOrUpdatedProcedureService();
const deleteProcedureService = new DeleteProcedureService();

// Schema ajustado a todos los campos del formulario
// 1) Añade `id` al esquema para que yup lo conozca:
const ProceduresSchema = yup.object().shape({
  id: yup
    .number()
    .nullable(), // o string().nullable() si tu id viene como string
  procedureName: yup.string().required("Nombre es requerido"),
  description: yup.string().required("Descripción es requerido"),
});



export default function Procedures() {
  const headerStore = useHeaderStore(
    (state) => ({
      setModule: state.setModule,
      setModuleItem: state.setModuleItem,
    }),
    shallow
  );

  const [loading, setLoading] = useState(false);
  const [isReloadData, setIsReloadData] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isHoveredEdit, setIsHoveredEdit] = useState<number | null>(null);
  const [isOpenCurrentDelete, setIsOpenCurrentDelete] = useState<any>(null);
  const [isHoveredDelete, setIsHoveredDelete] = useState<number | null>(null);

  const [initialData, setInitialData] = useState<procedureData>({
    procedureId: null,
    procedureName: '',
    description: '',
    requirements: '',
    status: false,
  });

  const columns = [
    {
      header: "NOMBRE",
      field: "procedureName",
    },
    {
      header: "DESCRIPCIÓN",
      field: "description",
    },
    {
      header: "REQUERIMIENTOS",
      field: "requirements",
    },
    {
      header: "ESTADO",
      field: "status",
    },
    {
      header: "OPCIONES",
      field: "",
      render: (row: any) => (
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignContent: "center",
          gap: "5px",
        }}>

          <button
            className={styleDataTable.buttom_option}
            onClick={() => handleEdit(row)}
            onMouseEnter={() => setIsHoveredEdit(row.procedureId)}
            onMouseLeave={() => setIsHoveredEdit(null)}
          >
            <AppGetIcon
              name="edit"
              classIcon={styleDataTable.sizeIcon}
              strokeColor="#1196ce"
              hoverColor={isHoveredEdit === row.procedureId ? "#FFF" : undefined}
            />
            Editar
          </button>
          <button
            className={styleDataTable.buttom_option}
            onClick={() => {
              setIsOpenCurrentDelete(row);
            }}
            onMouseEnter={(e) => { setIsHoveredDelete(row?.procedureId); }}
            onMouseLeave={(e) => { setIsHoveredDelete(null); }}
          >
            <AppGetIcon
              name="trash"
              classIcon={styleDataTable.sizeIcon}
              strokeColor="#1196ce"
              hoverColor={isHoveredDelete === row?.id ? "#FFF" : ""}
            />{" "}
            Eliminar
          </button>
        </div>

      ),
    },
  ];

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteProcedureService.run(isOpenCurrentDelete?.procedureId,);
      toast.success(
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <AppGetIcon name="checkCircleFill" />
          Operación realizada correctamente
        </div>,
        { icon: false }
      );
      setIsReloadData(!isReloadData); // Recarga los datos después de la eliminación
      setIsOpenCurrentDelete(null); // Cierra el modal
      setLoading(false);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      toast.error(
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <AppGetIcon name="xCircleFill" />
          {errorMessage}
        </div>,
        { icon: false }
      );
      console.error("Error al eliminar:", error);
      setLoading(false);
    }
  };

  const handleCloseModalDelete = () => {
    // setInitialData(null);
    setIsOpenCurrentDelete(null);
  };


  // 2. En handleEdit, rellena también el id y organizationalJobId
  const handleEdit = (row: any) => {
    // ③ Finalmente seteas el estado inicial con esos valores:
    setInitialData({
      procedureId: row.procedureId,
      procedureName: row.procedureName,
      description: row.description,
      requirements: row.requirements,
      status: row.status === 'activo' ? true : false,

    });

    setIsEditMode(true);
    setIsOpen(true);
  };


  const handleCloseModal = () => {
    setIsOpen(false);
    setInitialData({
      procedureId: null,
      procedureName: '',
      description: '',
      requirements: '',
      status: false,
    });
    setIsEditMode(false);
  }
  // 3. Llama al servicio en handleSubmit
  const handleSubmit = async (values: typeof initialData) => {
    setLoading(true);


    const payload: any = {
      procedureId: isEditMode ? Number(values.procedureId) : null,
      procedureName: values.procedureName,
      description: values.description,
      requirements: values.requirements,
      status: values.status === true ? 'activo' : 'inactivo',
    };

    try {
      const result = await createOrUpdatedProcedureService.run(payload, payload.procedureId);
      toast.success(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="checkCircleFill" />
          {result.message}
        </div>,
        { icon: false }
      );
      setIsReloadData(prev => !prev);
      setIsOpen(false);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || "Error";
      toast.error(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="xCircleFill" />
          {errorMessage}
        </div>,
        { icon: false }
      );
    } finally {
      setLoading(false);
    }
  };

  const addButton = () => {
    setIsOpen(true);
  };
  useEffect(() => {
    headerStore.setModule("Administración", "");
    headerStore.setModuleItem("Administración de trámites", "/admin/home");
  }, [headerStore]);

  return (
    <div className={style.containerQr}>
      <DataTable
        title="Lista de trámites"
        columns={columns}
        service={getProceduresWithPaginationService}
        reloadData={isReloadData}
        addButton={addButton}
        excelButton={{
          enabled: true,
          nameFile: `Lista de trámites`,
          getExcelData: (data) => {
            return data.map((row) => {
              // 1) Sacamos el array correcto (roleUser o rolesUser)
              const userRoles = row.roleUser || row.rolesUser || [];
              // 2) Extraemos sólo el nombre de cada rol
              const rolesList = userRoles.map((ru: any) => ru.role.name).join(", ");

              return {
                NOMBRES: row.procedureName ?? "",
                DESCRIPCIÓN: row.description ?? "",
                REQUERIMIENTOS: row.requirements,
                ESTADO: row.isActive ? "Activado" : "Desactivado",
              };
            });
          },
        }}
      >{''}</DataTable>

      <Modal
        title={isEditMode ? "Editar trámite" : "Crear trámite"}
        isOpen={isOpen}
        onClose={handleCloseModal}
        size={{ width: "80%", maxWidth: "1200px", height: "" }}
        loadingModal={loading}
        footerContent={
          <div className={style.containerButtoms}>
            <button
              className={styleForm.buttomFooter + " " + styleForm.buttom_cancelar}
              onClick={handleCloseModal}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={styleForm.buttomFooter}
              form="userForm"
              disabled={loading}
            >
              Guardar
            </button>
          </div>
        }
      >
        <Formik
          initialValues={initialData}
          validationSchema={ProceduresSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ values }) => (

            <Form id="userForm" className="flex-row">
              <div className={styleForm.form__container_row}>

                <InputText classNameInput={styleForm.input__input} type="text" name="procedureName" placeholder="Nombre" TextLabel="Nombre*" />
              </div>

              <div className={styleForm.form__container_row}>
                <InputTextArea
                  name="description"
                  classNameInput={styleForm.input__inputWide}
                  placeholder="Descripción"
                  TextLabel="Descripción*"
                />
              </div>
              <div className={styleForm.form__container_row}>
                <InputTextArea
                  name="requirements"
                  classNameInput={styleForm.input__inputWide}
                  placeholder="Requerimientos"
                  TextLabel="Requerimientos"
                />
              </div>
              <div style={{ paddingTop: '10px' }}>
                <label className={style.title}>Activo</label>
                <Field type="checkbox" name="status" />
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
      {/* ✅ Modal de confirmación de eliminado (nuevo componente) */}
      <DeleteConfirmationModal
        isOpen={Boolean(isOpenCurrentDelete)}
        onClose={handleCloseModalDelete}
        onConfirm={handleDelete}
        loading={loading}
        itemName={isOpenCurrentDelete?.procedureName}
        customMessage="¿Está seguro que desea eliminar este trámite?"
      />
    </div>
  );
}